import MaterialBuilder from "../rendering/material-builder";
import RenderManager from "../rendering/render-manager";
import MaterialAtom from "../rendering/material-atom";
import { fire } from "../utils/events";

class Atom extends THREE.Object3D {
    constructor(p = 0, n = 0, e = 0) {
        super();

        this.isAtom = true;
        this.isUpdatable = true;
        this.type = "Atom";

        this._protons = p;
        this._neutrons = n;
        this._electrons = e;
        this._nucleusRadius = 0;

        this._nucleus = null;
        this._shell = null;
        this._quants = [];

        this._time = 0;
        this._activeTimeline = null;
        this._infoTextbox = null;

        this._enablePeriodicTable = false;
        this._interactiveTable = false;
        this._infoBoxVisible = true;
        this._infoLabelsVisible = true;
        this._controlsVisible = true;

        this._buildAtom();
        this._createTimeline();
        let visible = this.visible;
        Object.defineProperty(this, "visible", {
            get: () => visible,
            set: (enabled) => {
                visible = enabled;
                fire("atom_toggle_visibility", this);
            },
        });
    }


    _buildAtom() {
        while (this.children.pop()) { }

        const { nucleus, radius } = createNucleus(this);
        this._nucleus = nucleus;
        this._nucleusRadius = radius;

        const { quants, shell } = createElectronShell(this, radius);
        this._quants = quants;
        this._shell = shell;
    }

    update() {
        this._quants.forEach(quant => {
            quant.rotateY(THREE.Math.RAD2DEG * 0.001);
            quant.rotateX(THREE.Math.RAD2DEG * 0.001);

            if (!USE_VOLUME) quant.electrons.forEach(x => x.material.uniforms.time.value = Math.abs(Math.cos(this._time)));
        });

        if (!USE_VOLUME) {
            this._nucleus.protons.forEach(x => x.material.uniforms.time.value = Math.abs(Math.sin(this._time)));
            this._nucleus.neutrons.forEach(x => x.material.uniforms.time.value = Math.abs(Math.cos(this._time)));
        }

        this._nucleus.rotateX(THREE.Math.RAD2DEG * 0.0001);
        this._nucleus.rotateZ(THREE.Math.RAD2DEG * 0.0001);

        this._nucleus.position.set(0.1 * Math.cos(this._time), 0, 0.1 * Math.sin(this._time))

        this._time += 0.05;
        RenderManager.containsAnimatedObjects = true;

    }

    _createTimeline() {
        const positions = calculateFinalPosition(this, 200);

        if (this._activeTimeline) this._activeTimeline.kill();

        const timeline = this._activeTimeline = new TimelineLite();
        const time = 0.25;

        positions.forEach((target, i) => {
            const particle = this._nucleus.children[i].position;
            const source = { x: particle.x, y: particle.y, z: particle.z };
            const destination = {
                x: target.x,
                y: target.y,
                z: target.z,
                onUpdate() { particle.set(source.x, source.y, source.z); }
            };

            const tween = new TweenMax(source, time, destination);

            timeline.add(tween, 0);
        });

        this._quants.forEach((quant, i) => {
            const source = { radius: quant.edge.scale.x };
            const levelRadius = calculateShellRadius(this._nucleusRadius, i);
            const thethaOffset = quant.thethaOffset;
            const increments = calcIncrements(quant.electrons.length);
            const destination = {
                radius: levelRadius,
                onUpdate() {
                    quant.edge.scale.set(source.radius, source.radius, source.radius);

                    quant.electrons.forEach((el, i) => {
                        calculateElectronPosition(source.radius, thethaOffset + i * increments, el.position);
                    });
                }
            };

            const tween = new TweenMax(source, time, destination);

            timeline.add(tween, 0);
        });

        timeline.add(() => {
            this._activeTimeline = null;
        });
    }

    addProton() {
        this._protons++;
        Q3.data.assets.objects[this.uuid].counts.proton = this._protons;

        this._nucleusRadius = addToNucleus(this, "proton");

        this._createTimeline();

        fire("atom_particle_count_changed", this);
    }

    addNeutron() {
        this._neutrons++;

        Q3.data.assets.objects[this.uuid].counts.neutron = this._neutrons;

        this._nucleusRadius = addToNucleus(this, "neutron");
        this._createTimeline();

        fire("atom_particle_count_changed", this);
    }

    addElectron() {
        this._electrons++;
        Q3.data.assets.objects[this.uuid].counts.electron = this._electrons;

        const { quant, electron } = addElectron(this);

        const timeline = new TimelineLite();
        const time = 5, hTime = time * 0.5;

        const highlightColor = 0xff0000;
        const normalColor = 0x2222ff;

        (function () {
            const rgbStart = new THREE.Color(normalColor);
            const rgbFinish = new THREE.Color(highlightColor);

            const source = { r: rgbStart.r, g: rgbStart.g, b: rgbStart.b };
            const destination = {
                r: rgbFinish.r,
                g: rgbFinish.g,
                b: rgbFinish.b,
                onStart() {
                    quant.edge.material.depthTest = electron.material.depthTest = false;
                    quant.edge.material.depthWrite = electron.material.depthWrite = false;
                    quant.edge.material.transparent = electron.material.transparent = true;
                },
                onUpdate() {
                    quant.edge.material.color.setRGB(source.r, source.g, source.b);
                    electron.material.color.setRGB(source.r, source.g, source.b);
                }
            };

            const tween = new TweenMax(source, 0.2, destination);

            timeline.add(tween, 0);
        })();

        (function () {
            const rgbStart = new THREE.Color(highlightColor);
            const rgbFinish = new THREE.Color(normalColor);

            const source = { r: rgbStart.r, g: rgbStart.g, b: rgbStart.b };
            const destination = {
                r: rgbFinish.r,
                g: rgbFinish.g,
                b: rgbFinish.b,
                onUpdate() {
                    quant.edge.material.color.setRGB(source.r, source.g, source.b);
                    electron.material.color.setRGB(source.r, source.g, source.b);
                },
                onCompleted() {
                    quant.edge.material.depthTest = electron.material.depthTest = true;
                    quant.edge.material.depthWrite = electron.material.depthWrite = true;
                    quant.edge.material.transparent = electron.material.transparent = false;
                },
            };

            const tween = new TweenMax(source, 0.5, destination);

            timeline.add(tween, hTime);
        })();

        fire("atom_particle_count_changed", this);
    }

    removeProton() {
        if (this._protons - 1 <= 0) return;

        this._protons--;

        Q3.data.assets.objects[this.uuid].counts.proton = this._protons;

        this._nucleusRadius = removeFromNucleus(this, "proton");
        this._createTimeline();

        fire("atom_particle_count_changed", this);
    }

    removeNeutron() {
        if (this._neutrons - 1 < 0) return;

        this._neutrons--;

        Q3.data.assets.objects[this.uuid].counts.neutron = this._neutrons;

        this._nucleusRadius = removeFromNucleus(this, "neutron");
        this._createTimeline();

        fire("atom_particle_count_changed", this);
    }

    removeElectron() {
        if (this._electrons - 1 < 0) return;

        this._electrons--;
        Q3.data.assets.objects[this.uuid].counts.electron = this._electrons;

        removeElectron(this);

        fire("atom_particle_count_changed", this);
    }

    get protons() { return this._protons; }
    get neutrons() { return this._neutrons; }
    get electrons() { return this._electrons; }

    setParticleCount(p = 0, n = 0, e = 0) {
        this._protons = p;
        this._neutrons = n;
        this._electrons = e;

        Q3.data.assets.objects[this.uuid].counts.proton = this._protons;
        Q3.data.assets.objects[this.uuid].counts.neutron = this._neutrons;
        Q3.data.assets.objects[this.uuid].counts.electron = this._electrons;

        this._buildAtom();
        fire("atom_particle_count_changed", this);
    }

    get interactiveTable() { return this._interactiveTable; }
    set interactiveTable(interactive) {
        interactive = Boolean(interactive);

        if (this._interactiveTable == interactive) return;

        this._interactiveTable = interactive;
        fire("atom_toggle_interactive_table", this);
    }

    get enablePeriodicTable() { return this._enablePeriodicTable; }
    set enablePeriodicTable(enabled) {
        enabled = Boolean(enabled);
        if (enabled == this._enablePeriodicTable) return;

        this._enablePeriodicTable = enabled;
        fire("atom_toggle_periodic_table", this);
    }

    get infoBoxVisible() { return this._infoBoxVisible; }
    set infoBoxVisible(visible) {
        visible = Boolean(visible);
        if (visible == this._infoBoxVisible) return;

        this._infoBoxVisible = visible;
        fire("atom_toggle_info_box", this);
    }

    get infoLabelsVisible() { return this._infoLabelsVisible; }
    set infoLabelsVisible(visible) {
        visible = Boolean(visible);
        if (visible == this._infoLabelsVisible) return;

        this._infoLabelsVisible = visible;
        fire("atom_toggle_info_labels", this);
    }

    get controlsVisible() { return this._controlsVisible; }
    set controlsVisible(visible) {
        visible = Boolean(visible);
        if (visible == this._controlsVisible) return;

        this._controlsVisible = visible;
        fire("atom_toggle_controls", this);
    }

}

export default Atom;
export { Atom };

const USE_VOLUME = true;

function createGeometry(scale) {
    if (USE_VOLUME) return new THREE.SphereBufferGeometry(scale, 64, 64);

    return new THREE.PlaneBufferGeometry(1, 1);
}

function createMaterial(color, scale) {
    if (USE_VOLUME) return MaterialBuilder.build({ color, materialType: "pbr" });

    return new MaterialAtom({ color, scale })
}

function createMesh(geometry, material) {
    if (USE_VOLUME) return new THREE.Mesh(geometry, material);

    const point = new THREE.Mesh(geometry, material);

    point.renderOrder = Infinity;

    return point;
}

const { createNucleus, addToNucleus, removeFromNucleus } = (function () {
    const geometryNucleus = createGeometry(1);
    let materialProton, materialNeutron;

    const twoPI = Math.PI * 2;
    const fourPI = twoPI * 2;
    const particleVolume = fourPI / 3;

    function calculatePointInNucleus(a, b, out) {
        const a3 = a * a * a;
        const b3 = b * b * b;

        const r1 = Math.cbrt((Math.random() * (b3 - a3) + a3));
        const phi1 = Math.acos(-1 + 2 * Math.random());
        const th1 = twoPI * Math.random();

        // Convert to cart.
        const x = r1 * Math.sin(phi1) * Math.sin(th1);
        const y = r1 * Math.sin(phi1) * Math.cos(th1);
        const z = r1 * Math.cos(phi1);

        out.set(x, y, z);
    }

    function calcRadius(protons, neutrons) {
        const volume = (particleVolume * protons + particleVolume * neutrons) * 0.5;
        const radius = Math.cbrt(3 * volume / fourPI);

        return radius;
    }

    function removeFromNucleus(atom, type) {
        const nucleus = atom._nucleus;

        const protons = atom._protons;
        const neutrons = atom._neutrons;

        const radius = calcRadius(protons, neutrons);

        const array = type === "proton" ? nucleus.protons : nucleus.neutrons;

        const particle = array.shift();

        if (particle) nucleus.remove(particle);

        return radius;
    }

    function addToNucleus(atom, type) {
        const nucleus = atom._nucleus;
        const protons = atom._protons;
        const neutrons = atom._neutrons;
        const radius = calcRadius(protons, neutrons);
        const mesh = createMesh(geometryNucleus, type === "proton" ? materialProton : materialNeutron);
        const array = type === "proton" ? nucleus.protons : nucleus.neutrons;

        array.push(mesh);

        calculatePointInNucleus(0, radius * 10, mesh.position);

        nucleus.add(mesh);

        return radius;
    }

    function createNucleus(atom) {
        const nucleus = new THREE.Group();

        nucleus.protons = [];
        nucleus.neutrons = [];

        nucleus.dontShowInHierarchyPanel = true;

        const protons = atom._protons;
        const neutrons = atom._neutrons;
        const radius = calcRadius(protons, neutrons);

        materialProton = materialProton || createMaterial(0xff2222, 1);
        materialNeutron = materialNeutron || createMaterial(0x222222, 1);

        for (let i = 0; i < protons; i++) {
            const mesh = createMesh(geometryNucleus, materialProton);

            calculatePointInNucleus(0, radius, mesh.position);

            nucleus.add(mesh);
            nucleus.protons.push(mesh);
        }

        for (let i = 0; i < neutrons; i++) {
            const mesh = createMesh(geometryNucleus, materialNeutron);

            calculatePointInNucleus(0, radius, mesh.position);

            nucleus.add(mesh);
            nucleus.neutrons.push(mesh);
        }

        atom.add(nucleus);

        return { radius, nucleus };
    }

    return { createNucleus, addToNucleus, removeFromNucleus };
})();

const { createElectronShell, calculateShellRadius, calculateElectronPosition, calcIncrements, addElectron, removeElectron } = (function () {
    const sizeElectron = 0.2;
    const edgeMaterial = new THREE.MeshBasicMaterial({ color: 0x2222ff });
    const geometryElectron = createGeometry(sizeElectron);
    let materialElectron;

    const circleGeometry = new THREE.CircleBufferGeometry(1, 64);
    circleGeometry.rotateX(Math.PI * 0.5);
    const edgeGeometry = new THREE.EdgesGeometry(circleGeometry);

    const twoPI = Math.PI * 2;
    const electronsPerShell = [2, 8, 18, 32, 50, 1000000];
    const tiltAxis = new THREE.Vector3(0, 0, 1);
    const quaternion = new THREE.Quaternion();

    function calculateElectronPosition(radius, thetha, out) {
        const x = radius * Math.cos(thetha);
        const y = radius * Math.sin(thetha);

        out.set(x, 0, y);
    }

    function calculateShellRadius(nucelusRadius, shellIndex) {
        return nucelusRadius + (nucelusRadius * 1.2 * (shellIndex + 1));
    }

    function calcIncrements(electronsInShell) { return twoPI / electronsInShell; }

    function removeElectron(atom) {
        const shell = atom._quants.length - 1;
        const quant = atom._quants[shell];

        const currentlyInShell = quant.electrons.length;

        const removeShell = currentlyInShell - 1 <= 0;

        if (removeShell) {
            atom._quants.pop();
            quant.parent.remove(quant);
        } else {
            const electron = quant.electrons.pop();
            electron.parent.remove(electron);

            const thethaOffset = quant.thethaOffset;
            const increments = calcIncrements(currentlyInShell - 1);
            const levelRadius = calculateShellRadius(atom._nucleusRadius, shell);

            quant.electrons.forEach((el, i) => {
                calculateElectronPosition(levelRadius, i * increments + thethaOffset, el.position);
            });
        }
    }

    function addElectron(atom) {
        let shell = atom._quants.length - 1;
        let quant = atom._quants[shell];
        let allowedInShell = electronsPerShell[shell];
        let currentlyInShell = quant ? quant.electrons.length : 0;

        const needsNewShell = !quant || currentlyInShell + 1 > allowedInShell;

        const mesh = createMesh(geometryElectron, materialElectron.clone());

        if (!needsNewShell) {
            quant.add(mesh);
            quant.electrons.push(mesh);
        } else {
            shell++;
            quant = new THREE.Group();
            currentlyInShell = 0;
            allowedInShell = electronsPerShell[shell];
            quant.electrons = [mesh];
            quant.thethaOffset = THREE.Math.mapLinear(Math.random(), 0, 1, -Math.PI, Math.PI);

            const tilt = THREE.Math.mapLinear(Math.random(), 0, 1, -Math.PI, Math.PI);
            quaternion.setFromAxisAngle(tiltAxis, tilt);

            quant.quaternion.copy(quaternion);

            atom._quants.push(quant);
            atom._shell.add(quant);

            quant.add(mesh);
        }

        const thethaOffset = quant.thethaOffset;
        const increments = calcIncrements(currentlyInShell + 1);
        const levelRadius = calculateShellRadius(atom._nucleusRadius, shell);

        if (needsNewShell) {
            const edge = new THREE.LineSegments(edgeGeometry, edgeMaterial.clone());
            edge.scale.set(levelRadius, levelRadius, levelRadius);

            quant.add(edge);

            quant.edge = edge;
        }

        quant.electrons.forEach((el, i) => {
            calculateElectronPosition(levelRadius, i * increments + thethaOffset, el.position);
        });

        return { quant, electron: mesh };
    }

    function createElectronShell(atom, radius) {
        let electrons = atom._electrons;
        let shell = 0;
        const electronShell = new THREE.Group();
        const quants = [];

        electronShell.dontShowInHierarchyPanel = true;

        while (electrons > 0) {
            const allowedInShell = electronsPerShell[shell];
            const electronsLeft = electrons - allowedInShell;
            const inShell = electronsLeft >= 0 ? allowedInShell : allowedInShell + electronsLeft;
            electrons = Math.max(0, electronsLeft);

            if (inShell > 0) {
                const quant = new THREE.Group();
                electronShell.add(quant);
                quants.push(quant);
                quant.electrons = [];

                const increments = calcIncrements(inShell);
                materialElectron = materialElectron || createMaterial(0x2222ff, sizeElectron);

                const tilt = THREE.Math.mapLinear(Math.random(), 0, 1, -Math.PI, Math.PI);
                quaternion.setFromAxisAngle(tiltAxis, tilt);
                const thethaOffset = quant.thethaOffset = THREE.Math.mapLinear(Math.random(), 0, 1, -Math.PI, Math.PI);

                const levelRadius = calculateShellRadius(radius, shell);

                quant.quaternion.copy(quaternion);

                for (let i = 0; i < inShell; i++) {
                    const mesh = createMesh(geometryElectron, materialElectron.clone());

                    calculateElectronPosition(levelRadius, i * increments + thethaOffset, mesh.position);

                    quant.add(mesh);
                    quant.electrons.push(mesh);
                }

                const edge = new THREE.LineSegments(edgeGeometry, edgeMaterial.clone());
                edge.scale.set(levelRadius, levelRadius, levelRadius);

                quant.add(edge);

                quant.edge = edge;
            }

            shell++;
        }

        atom.add(electronShell);

        return { shell: electronShell, quants };
    };

    return { createElectronShell, calculateShellRadius, calculateElectronPosition, calcIncrements, addElectron, removeElectron };
})();

const calculateFinalPosition = function (atom, iterations) {
    const positions = atom._nucleus.children.map(ch => new THREE.Vector3().copy(ch.position));
    const radius = atom._nucleusRadius;

    for (let i = 0; i < iterations; i++) calculateStrongNuclearForce(radius, positions);

    return positions;
}

const calculateStrongNuclearForce = (function () {
    const force = -0.4;
    const particleRadius = 0.7;
    const tmp = new THREE.Vector3();

    return function (radius, positions) {

        positions.reduce((accum, particle) => {
            const position = new THREE.Vector3();

            position.copy(particle).setLength(force).add(particle);

            positions.forEach((other, i) => {
                if (other === particle) return;

                tmp.subVectors(position, positions[i]);

                const len = tmp.length();

                if (len > particleRadius) return;

                position.add(tmp);
            });

            accum.push({ particle, position });

            return accum;
        }, []).forEach(result => {
            result.particle.copy(result.position);
        });
    }
})();
